interface SheetCardProps {
  sheet: {
    id: string;
    name: string;
    description: string;
    imageUrl?: string;
    bonuses: Array<{ attribute: string; value: number }>;
    maluses: Array<{ attribute: string; value: number }>;
    moves: Array<{ name: string; description: string }>;
    notes?: string;
    damage?: number;
    armor?: number;
    maxHP?: number;
    level?: number;
  };
  onEdit: () => void;
  onDelete: (sheetId: string) => void;
}

export function SheetCard({ sheet, onEdit, onDelete }: SheetCardProps) {
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 hover:border-purple-500 transition overflow-hidden">
      {/* Image */}
      {sheet.imageUrl ? (
        <div
          className="h-48 bg-cover bg-center"
          style={{ backgroundImage: `url(${sheet.imageUrl})` }}
        />
      ) : (
        <div className="h-48 bg-gradient-to-br from-purple-900 to-gray-900 flex items-center justify-center">
          <span className="text-6xl">🎭</span>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Name */}
        <h3 className="text-2xl font-bold text-white mb-2">{sheet.name}</h3>

        {/* Description */}
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
          {sheet.description}
        </p>

        {/* Combat Stats */}
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded">
            Danno: {sheet.damage ?? 0}
          </span>
          <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded">
            Armatura: {sheet.armor ?? 0}
          </span>
          <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded">
            PF: {sheet.maxHP ?? 10}
          </span>
          <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded">
            Lv: {sheet.level ?? 1}
          </span>
        </div>

        {/* Bonuses */}
        {sheet.bonuses.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-green-400 mb-1">BONUSES</p>
            <div className="flex flex-wrap gap-1">
              {sheet.bonuses.map((bonus, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-green-900 bg-opacity-30 text-green-300 text-xs px-2 py-1 rounded"
                >
                  {bonus.attribute} {bonus.value > 0 ? '+' : ''}{bonus.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Maluses */}
        {sheet.maluses.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-red-400 mb-1">MALUSES</p>
            <div className="flex flex-wrap gap-1">
              {sheet.maluses.map((malus, idx) => (
                <span
                  key={idx}
                  className="inline-block bg-red-900 bg-opacity-30 text-red-300 text-xs px-2 py-1 rounded"
                >
                  {malus.attribute} {malus.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Moves Count */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-blue-400 mb-1">SPECIAL MOVES</p>
          <p className="text-gray-400 text-sm">
            {sheet.moves.length} {sheet.moves.length === 1 ? 'move' : 'moves'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t border-gray-700">
          <button
            onClick={onEdit}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(sheet.id)}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded transition text-sm"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
